import { Pricings, EventDescription } from './types/event'
import {
  getPricingPricePerPerson,
  getPricingBasePrice,
  getPricingTotalPrice,
  getPricingAppliedDiscount,
} from '~/utils/pricingGetters'
import { eventGetters } from '~/utils/eventGetters'

export const useGetPricingKey = ({
  dateId = ref(undefined),
  quantity,
  event,
}: {
  dateId: Ref<string | undefined>
  quantity: Ref<number>
  event: Ref<EventDescription | undefined>
}) => {
  const isSpecialGiftcard = computed(() =>
    eventGetters.isGiftcardEvent(event.value)
  )
  const isBoxEvent = computed(() => eventGetters.isBoxEvent(event.value))

  // Check if we should use single pricing (box events or special giftcard events)
  const shouldUseSinglePricing = computed(
    () => isBoxEvent.value || isSpecialGiftcard.value
  )

  const pricingKey = computed(() => {
    if (shouldUseSinglePricing.value) {
      return `${quantity.value}`
    }

    return `${quantity.value}-${dateId.value}`
  })

  return {
    shouldUseSinglePricing,
    pricingKey,
  }
}

export const useEventPricingsGetters = (
  pricingsData: Ref<Record<string, Pricings>>,
  {
    dateId = ref(undefined),
    quantity,
    event,
  }: {
    dateId: Ref<string | undefined>
    quantity: Ref<number>
    event: Ref<EventDescription | undefined>
  }
) => {
  const { shouldUseSinglePricing, pricingKey } = useGetPricingKey({
    dateId,
    quantity,
    event,
  })

  // Get the pricing object to use
  const pricingData = computed(() => pricingsData.value[pricingKey.value])

  const unitPrice = computed(() => {
    if (!pricingData.value) {
      return undefined
    }

    return getPricingPricePerPerson(pricingData.value, 'ADULT')
  })

  const basePrice = computed(() => {
    if (!pricingData.value) {
      return undefined
    }
    return getPricingBasePrice(pricingData.value)
  })

  const totalPrice = computed(() => {
    if (!pricingData.value) {
      return undefined
    }
    return getPricingTotalPrice(pricingData.value).formatted
  })

  const appliedDiscount = computed(() => {
    if (!pricingData.value) {
      return undefined
    }
    return getPricingAppliedDiscount(pricingData.value)
  })

  const hasSale = computed(() => {
    return (
      appliedDiscount.value !== undefined &&
      Number(appliedDiscount.value.amount) > 0
    )
  })

  return {
    pricingKey,
    pricingData,
    unitPrice,
    basePrice,
    totalPrice,
    appliedDiscount,
    hasSale,
    shouldUseSinglePricing,
  }
}
